/**
 * Ficha de operador do painel direito — DUAS CAMADAS.
 *
 * Camada 1, sempre presente: identidade e papel, de `users/:id`.
 * Camada 2, condicional: presença, carga e padrão comportamental, do estado do
 * painel de equipe — que cobre uma JANELA de atendentes.
 *
 * Quando a camada 2 não existe, a ficha DIZ isso em vez de mostrar campos
 * vazios. O supervisor nunca busca alguém que existe e recebe «não encontrado»,
 * e nunca vê painel vazio fingindo ser dado. É a terceira saída para o limite 2
 * da spec — as duas que a spec propunha eram ruins.
 *
 * D1 — localizador puro: as duas ações NAVEGAM. «Mensagem privada», que o
 * mockup da rodada 1 mostrava, ficou FORA de propósito: abrir composição de
 * mensagem é superfície de mutação, e mutação não mora na paleta. Ela continua
 * disponível no destino.
 */
import { AlertTriangle, BarChart3, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { UserAvatar } from '../../components/UserAvatar';
import { useCapability } from '../../hooks/use-capability';
import { useUser } from '../../hooks/use-users';

import { useOperatorLiveSignals } from './use-operator-live-signals';

const ROLE_LABELS: Record<string, string> = {
  atendente: 'Atendente',
  supervisor: 'Supervisor',
  gestor: 'Gestor',
  admin_tenant: 'Admin Tenant',
  dpo_tenant: 'DPO',
  suporte_lume: 'Suporte Lume',
  engenharia_lume: 'Engenharia Lume',
  direcao_lume: 'Direção Lume',
};

const PRESENCE_LABELS: Record<string, string> = {
  online: 'online',
  away: 'ausente',
  offline: 'offline',
  busy: 'ocupado',
};

export function OperatorHitCard({ userId }: { userId: string }): JSX.Element {
  const navigate = useNavigate();
  const { data: user } = useUser(userId);
  const { data: live } = useOperatorLiveSignals(userId);
  const canSeeQuality = useCapability('quality-aggregations-read');

  const name = user?.name ?? '—';
  const role = user !== undefined ? (ROLE_LABELS[user.primaryRole] ?? user.primaryRole) : '';

  return (
    <div className="spotlight__op">
      <div className="spotlight__op-head">
        <UserAvatar name={name} colorKey={userId} size="md" />
        <div className="spotlight__op-id">
          <span className="spotlight__op-name">{name}</span>
          <span className="spotlight__op-role">
            {role}
            {live !== undefined && (
              <>
                {' · '}
                <span className={`spotlight__dot spotlight__dot--${live.presence}`} aria-hidden />
                {PRESENCE_LABELS[live.presence] ?? live.presence}
              </>
            )}
          </span>
        </div>
      </div>

      {live !== undefined ? (
        <dl className="spotlight__kv">
          <div className="spotlight__kv-row">
            <dt>Carga</dt>
            <dd>
              <b>
                {live.currentLoad} de {live.capacity}
              </b>{' '}
              conversas
              {live.iaConducting !== undefined && live.iaConducting > 0 && (
                <> · {live.iaConducting} com IA conduzindo</>
              )}
            </dd>
          </div>
          {live.behavioralPattern !== undefined && live.behavioralPattern !== null && (
            <div className="spotlight__kv-row">
              <dt>Atenção</dt>
              <dd>
                <span className="spotlight__flag">
                  <AlertTriangle size={11} aria-hidden />
                  {live.behavioralPattern.label}
                </span>
              </dd>
            </div>
          )}
        </dl>
      ) : (
        <p className="spotlight__op-degraded">
          Carga, SLA e presença estão fora da janela do painel de equipe para este operador.
        </p>
      )}

      <div className="spotlight__op-actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => navigate('/supervisor/acompanhamento')}
        >
          <Eye size={12} aria-hidden /> Acompanhar
        </button>
        {canSeeQuality && (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => navigate('/supervisor/qualidade')}
          >
            <BarChart3 size={12} aria-hidden /> Ver qualidade
          </button>
        )}
      </div>
    </div>
  );
}
